/* eslint-disable security/detect-object-injection */
/* eslint-disable radix */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { INodeProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IProductCollectionViewProps, IProductComponentViewProps } from './product-collection';
import { IProductCollectionResources } from './product-collection.props.autogenerated';
import { ProductSearchResult, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import ProductPopover from '../../themes/jansen-adventureworks/views/components/product-popover.component';
import { AddToCartComponent } from '@msdyn365-commerce/components';
import { useState } from 'react';
import IncrementalQuantity from '../../themes/jansen-adventureworks/views/components/IncrementalQuantity';

import { Toaster, toast } from 'sonner';
import Button from 'reactstrap/lib/Button';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';

const _showToast = (heading: string, description: string) => {
    toast.error(
        <div className='toast' role='alert'>
            <div className='toast-header'>
                <h4>{heading}</h4>
            </div>
            <div className='toast-body'>
                <span>{description}</span>
            </div>
        </div>
    );
};

const ProductCollectionView: React.FC<IProductCollectionViewProps> = props => {
    const {
        heading,
        productsWithOutComponent,
        ProductCollectionContainer,
        products,
        SingleSlideCarouselComponentProps,
        GridComponentProps,
        isCarousel,
        isList,
        seeAllButton,
        resources
    } = props;

    if (products) {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {isList
                    ? _renderList(productsWithOutComponent, props)
                    : isCarousel
                    ? _renderCarousel(SingleSlideCarouselComponentProps, products)
                    : _renderGrid(GridComponentProps, products)}
            </Module>
        );
    }

    // If no products are retrieved and the list type is promotional, still render the module to keep the entry of promotion page
    if (props.config.productCollection.listType === 'promotional') {
        return (
            <Module {...ProductCollectionContainer}>
                {heading}
                {seeAllButton}
                {_renderNoProduct(resources)}
            </Module>
        );
    }
    props.context.telemetry.error('Product collection content is empty, module wont render.');
    return null;
};

interface errorMessage {
    LocalizedMessageParameters: string;
}

const _renderList = (items: ProductSearchResult[], props: React.PropsWithChildren<IProductCollectionViewProps>): JSX.Element => {
    const { context, typeName, id, resources } = props;
    const [quantities, setQuantities] = useState<number[]>(new Array(items?.length).fill(1));

    const handleQuantityChange = (index: number, value: number) => {
        const newQuantities = [...quantities];
        // eslint-disable-next-line security/detect-object-injection
        newQuantities[index] = value < 1 ? 1 : value; // Ensure quantity is at least 1
        setQuantities(newQuantities);
    };
    return (
        <>
            <Toaster
                richColors
                position='top-right'
                toastOptions={{
                    classNames: {
                        error: 'toast-danger'
                    }
                }}
            />
            <Node className={''}>
                <div className='table-responsive'>
                    <table className='ms-search-result-container__product-table table table-sm'>
                        {items.map((product: ProductSearchResult, index: number) => {
                            const productUrl = product
                                ? getProductPageUrlSync(product.Name ?? '', product.RecordId, context.actionContext, undefined)
                                : '';
                            const simpleProduct: SimpleProduct = {
                                RecordId: product.RecordId,
                                Price: product.Price,
                                ProductTypeValue: 0,
                                BasePrice: product.BasePrice || 0,
                                AdjustedPrice: product.Price
                            };
                            return (
                                <tr key={index}>
                                    <td>
                                        <ProductPopover product={product} index={index} context={context} layout='imagelist' />
                                    </td>
                                    <td>
                                        <IncrementalQuantity
                                            id='ms-buybox__product-quantity-input'
                                            min={1}
                                            max={999}
                                            currentCount={quantities[index]}
                                            onChange={value => {
                                                handleQuantityChange(index, value);
                                                return true;
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <Button className='msc-add-to-cart' title='moreInformation' href={productUrl}>
                                            {resources.moreInformationText}
                                        </Button>
                                    </td>
                                    <td>
                                        <AddToCartComponent
                                            quantity={quantities[index]}
                                            id={id}
                                            context={context}
                                            typeName={typeName}
                                            addToCartText={resources.addToCartText}
                                            data={{ product: simpleProduct }}
                                            disabled={false}
                                            onError={error => {
                                                const obj = error.cartActionResult?.validationResults?.find(
                                                    x =>
                                                        x.ErrorResourceId ===
                                                        'Microsoft_Dynamics_Commerce_Runtime_ItemQuantityViolatesMultiplicityOfConfiguredNumber'
                                                );
                                                const object: errorMessage = obj as errorMessage;
                                                const param = object.LocalizedMessageParameters[0];

                                                if (param !== undefined) {
                                                    _showToast(
                                                        product.Name!,
                                                        props.resources.errorMessageForQtyValidation.replace('{param}', param)
                                                    );
                                                }
                                            }}
                                        ></AddToCartComponent>
                                    </td>
                                </tr>
                            );
                        })}
                    </table>
                </div>
            </Node>
        </>
    );
};

const _renderCarousel = (carouselContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node className={''}>{items && items.map(_renderProduct)}</Node>;
};

const _renderGrid = (gridContainer: INodeProps, items: IProductComponentViewProps[]): JSX.Element => {
    return <Node {...gridContainer}>{items && items.map(_renderProduct)}</Node>;
};

const _renderNoProduct = (resources: IProductCollectionResources): JSX.Element => {
    return (
        <Node className={''}>
            <p>{resources.noProductToRenderText}</p>
        </Node>
    );
};

const _renderProduct = (product: IProductComponentViewProps): JSX.Element => {
    const { ProductContainer, productComponent } = product;

    return <Node {...ProductContainer}>{productComponent}</Node>;
};

export default ProductCollectionView;
